import axios from 'axios';
import _ from 'lodash/lodash';
import router from '@/router';
import store from '@/store/store.js';
import AuthUtil from '@/utils/auth.util.js';
import EventUtil from '@/utils/event.util.js';
import { ToastProgrammatic as Toast } from 'buefy';

const http = axios.create({
    baseURL: `${process.env.API_URL}${process.env.API_VER}`,
    headers: {'X-Requested-With': 'XMLHttpRequest'},
    responseType: 'json',
    validateStatus: function (status) {

        return (status >= 200 && status < 300);
    },
});

// --------------------------------------------------------------
// START: Interceptors for AJAX requests and responses
// --------------------------------------------------------------

http.interceptors.request.use(

    function (config) {

        store.dispatch('ajaxLoading');

        // Include auth token with requests (precaution: relative URLs or API domain only)
        if (config.url.indexOf('//') < 0 || config.url.toLowerCase().indexOf(process.env.API_URL.toLowerCase()) == 0) {

            const token = AuthUtil.getAuthToken();

            if (token) config.headers['Authorization'] = `Bearer ${token}`;
        }

        return config;
    },

    function (error) {

        return Promise.reject(error);
    }
);

http.interceptors.response.use(

    function (response) {

        store.dispatch('ajaxLoading', false);

        // @todo - Check if a new auth token has been assigned
        /*
        const freshToken = _.get(response.headers, 'theRefreshedBearerToken');

        if (freshToken) AuthUtil.setAuthToken(freshToken);
        */

        // Do something with response data
        return response;

    },

    function (error) {
        if (axios.isCancel(error)) {
            return Promise.reject(new Error('Request cancelled'))
        }

        store.dispatch('ajaxLoading', false);

        let errors = [];

        // READ: https://github.com/axios/axios#handling-errors
        if (error.response) {

            // The request was made and the server responded with a status code
            // that is not covered by default or custom axios validateStatus()
            const { data, status, statusText } = error.response;

            EventUtil.$emit('responseError', data);

            switch (status) {

                case 401:
                case 403:

                    // If we receive a 40x while on on non-public pages then redirect to login
                    if ( ! _.get(router, 'currentRoute.meta.public')) {
                        const message = "Redirecting to login. Invalid or expired session.";
                        window.console && console.log(message);
                        Toast.open({
                            message,
                            type: 'is-danger'
                        });
                        localStorage.clear();
                        store.dispatch('Auth/clearToken');
                        // router.push({ name: 'login' });
                    }

                    break;

                case 422:

                    // Laravel 5.6+ has validation errors nested under an "errors" object, further nested by field name with array of messages
                    // e.g. errors: {password: ['The password field is required', 'The password must be at least 10 characters']}
                    // Lumen 5.6 renders validation errors object under "error.message" further nested by field name with array of messages
                    const validationErrors = _.get(data, 'errors') || _.get(data, 'error.message');

                    if (_.isObject(validationErrors)) {

                        _.forEach(validationErrors, (item, k) => {

                            const field_errors = _.isArray(item) ? item : [item];

                            errors = errors.concat(field_errors);
                        });
                    }

                    break;

                case 429:

                    const headers = error.response.headers;
                    const retry_after = _.get(error, 'response.headers.retry-after', 'a few');

                    errors.push(`Too many requests. Try again in ${retry_after} minutes.`);

                    break;

                default:
                    if (data.message) {
                        errors.push(data.message.replace(/\n/g, '<br />'));
                    } else {
                        errors.push(`HTTP error ${error.response.status} - Please try again later.`);
                    }
            }
        }
        else if (error.request) {

            // The request was made but no response was received
            // `error.request` is an instance of XMLHttpRequest in the browser so could try replay etc.
            errors.push("No response was received from remote server. Please try again later.");
        }
        else {

            // Something happened in setting up the request that triggered an Error
            errors.push(error.message);
        }

        if (errors.length > 0) EventUtil.$emit('validationErrors', errors);

        console.log(errors);

        return Promise.reject(new Error(errors.join('; ')));
    }
);

// --------------------------------------------------------------
// END: Interceptors for AJAX requests and responses
// --------------------------------------------------------------

export class ApiUtil {

    static get(url, data, config = {}) {

        return http.get(url, { params: data, ...config })
            .then((response) => Promise.resolve(response.data))
            .catch((error) => Promise.reject(error));
    }

    static post(url, data, config) {

        config = _.isPlainObject(config) ? config : {};

        return http.post(url, data, config)
            .then((response) => Promise.resolve(response.data))
            .catch((error) => Promise.reject(error));
    }

    static put(url, data, config = {}) {

        // Can't seem to get PUT to work with a FormData object...
        // https://github.com/laravel/framework/issues/13457
        // (so we loop through the FormData entries to make a plain object literal)
        if (data instanceof FormData) {

            let obj = {}

            for (let [k, v] of data.entries()) obj[k] = v;

            data = obj;
        }

        return http.put(url, data, config)
            .then((response) => Promise.resolve(response.data))
            .catch((error) => Promise.reject(error));
    }

    static patch(url, data, config) {

        return http.patch(url, data)
            .then((response) => Promise.resolve(response.data))
            .catch((error) => Promise.reject(error));
    }

    static delete(url, config = {}) {

        return http.delete(url, config)
            .then((response) => Promise.resolve(response.data))
            .catch((error) => Promise.reject(error));
    }

    static postDownload(url, data, saveAs) {
        saveAs = _.isString(saveAs) ? saveAs : 'file.blob';
        return http.post(url, data, { responseType: 'blob' })
            .then((response) => {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', saveAs);
            link.setAttribute('class', 'tmp-ajax-download'); // might need to target for cleanup?
            document.body.appendChild(link);
            link.click();
            return Promise.resolve(true);
          })
          .catch(err => Promise.resolve(err));
    }
}


// WEBPACK FOOTER //
// ./src/utils/api.util.js